import { flankCover, type BodyFit, type LiveryPainter, type LiveryViews } from './types';
import { clamp01, fbm, lerp, rng, smoothstep } from './noise';

/**
 * livery/dragon.ts (M144) — TERMINIO DRAGON, the showpiece wrap.
 *
 * Owner brief: "a realistic wrap that looks like an actual dark-green dragon
 * head, with yellow eyes, and the front of the vehicle is fire the dragon is
 * breathing out of its mouth."
 *
 * The car IS the dragon. Read along its length:
 *   nose → front arch   the FIRE: it pours over the nose, hood and fenders
 *                       and streams back, wreathing the snout
 *   the doors           the HEAD in profile on each flank, jaws open, the
 *                       jet leaving its throat between the teeth
 *   the rear            the NECK: big keeled scales, dorsal spines, horns
 * and from above the roof is the skull (crest plates, horn roots).
 *
 * It is painted in three views AND as a data atlas: the scales have relief
 * (they catch the garage lights as the car turns), the eye is wet, and the
 * fire and the eye GLOW — the game's bloom picks the fire up at night.
 *
 * Deterministic: one seed, so every player sees the same dragon.
 */

type G = CanvasRenderingContext2D;
type Pt = [number, number];
const SEED = 1987;

// ---------------------------------------------------------------------------
// Layout: where the head sits on THIS body.
// ---------------------------------------------------------------------------

interface Lay {
  fit: BodyFit;
  beltY: number;
  /** Snout tip (metres from the nose) and the head box. */
  mouthX: number;
  headLen: number;
  headTop: number;
  headH: number;
  /** Top view: hood / roof / deck boundaries. */
  cowl: number;
  tailGlass: number;
  /**
   * M153: how far the frill, and each swept-back horn (mid, long), may reach
   * behind the skull before the flank runs out, in head lengths past its back
   * (u = 1). Infinity on a head at its natural place (full length). M153b:
   * the frill's per spine (FRILL rows), so each one is cut by what is behind
   * it at its own height.
   */
  reach: { frill: number[]; mid: number; long: number };
  /**
   * M153b: where the top view's fire ends (metres from the nose). Infinity:
   * it runs back over the hood as at M144. On a ride whose head sits right
   * under a sky-facing facet (the motorbike's fairing) the top view's fire
   * would be painted over the brow, so it stops over the snout there.
   */
  topFireTo: number;
}

/** The frill's four spines: the row each roots on (head coordinates), its
 *  length in head lengths and how much it lifts. */
const FRILL: [number, number, number][] = [
  [0.45, 0.34, -0.05],
  [0.59, 0.27, -0.02],
  [0.72, 0.3, 0.01],
  [0.85, 0.2, 0.03],
];

/** M153b — rides whose head sits right under a sky-facing facet: the top
 *  view's fire stops this far behind the snout tip (head lengths). */
const TOP_FIRE_TO: Record<string, number> = { motorcycle: 0.32 };

/** A side-view rectangle in metres: x from the nose, y down from the roof line. */
interface Box {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  /** Not a part ON the flank: it hangs over it (seen from above, the chase
   *  angle) or marks where the flank turns away. The head's features must
   *  clear it; the frill and horns may run there. */
  overhang?: boolean;
  /** Only the EYE and the BROW must clear it: a mirror head standing just
   *  above the flank, which from the three-quarter views sits like a lump on
   *  the dragon's forehead (the snout and jaws may run under it). */
  brow?: boolean;
  /** Hides the flank from ONE camera angle only, the front three-quarter
   *  (the garage's hero angle): an open wheel standing out ahead of the
   *  flank. The eye and the brow must clear it; the snout and the jaws may be
   *  partly behind it, and the fit prefers the head that shows more teeth. */
  partial?: boolean;
}

/** Height of a side-view line (x, y pairs in metres, x ascending) at x. */
function lineAt(line: Pt[], x: number): number {
  if (x <= line[0][0]) return line[0][1];
  for (let i = 1; i < line.length; i++) {
    const [ax, ay] = line[i - 1];
    const [bx, by] = line[i];
    if (x <= bx) return lerp(ay, by, (x - ax) / Math.max(1e-9, bx - ax));
  }
  return line[line.length - 1][1];
}

/** Everything ABOVE a side-view line, as a run of thin boxes (each reaching
 *  the line's highest point over its width, so none overlaps the line). */
function aboveLine(line: Pt[], extra: Partial<Box>, step = 0.025): Box[] {
  const out: Box[] = [];
  const end = line[line.length - 1][0];
  for (let x = line[0][0]; x < end - 1e-9; x += step) {
    const x1 = Math.min(end, x + step);
    out.push({ x0: x, x1, y0: -1, y1: Math.max(lineAt(line, x), lineAt(line, x1)), ...extra });
  }
  return out;
}

/** Everything BELOW a side-view line, as a run of thin boxes. */
function belowLine(line: Pt[], extra: Partial<Box>, step = 0.025): Box[] {
  const out: Box[] = [];
  const end = line[line.length - 1][0];
  for (let x = line[0][0]; x < end - 1e-9; x += step) {
    const x1 = Math.min(end, x + step);
    out.push({ x0: x, x1, y0: Math.min(lineAt(line, x), lineAt(line, x1)), y1: 2, ...extra });
  }
  return out;
}

/**
 * M153 — the parts that stand PROUD of the painted flank and hide it. The
 * flank map only sees paint (and its holes); these are separate pieces over
 * it. Measured off the fitted models (every part rasterised over the paint in
 * the side view) and padded for the three-quarter views, where a part that
 * sticks out hides more of the flank below and beside it. The head is placed
 * so its eye, brow, snout and jaws land on paint none of these covers.
 */
const PARTS: Record<string, Box[]> = {
  van: [
    // The two side-exit pipes (centres 1.95 and 2.13 m, 0.76 m down, 0.14 m
    // proud of the shell): at the M144 place they came out of the jaws.
    { x0: 1.8, x1: 2.28, y0: 0.66, y1: 0.9 },
    // The dark slot on the flank behind the cab.
    { x0: 2.12, x1: 2.36, y0: 0.45, y1: 0.6 },
    // Above the shoulder crease (0.47 m down) the shell turns to the hood and
    // the deck: a brow painted there reads cut off from the jaw in profile.
    // (The horns may ride the shoulder, as they do on every ride.)
    { x0: 0.9, x1: 3.05, y0: -1, y1: 0.47, overhang: true },
  ],
  motorcycle: [
    // The lamp pocket cut into the nose of the fairing.
    { x0: -1, x1: 0.17, y0: 0.13, y1: 0.32 },
    // The rider's arm, from the bars down past the knee (its edge runs from
    // 0.62 m at the top of the fairing to 0.87 m, 0.3 m down).
    { x0: 0.59, x1: 1.2, y0: -1, y1: 0.07 },
    { x0: 0.64, x1: 1.2, y0: 0.07, y1: 0.15 },
    { x0: 0.71, x1: 1.2, y0: 0.15, y1: 0.22 },
    { x0: 0.78, x1: 1.2, y0: 0.22, y1: 0.31 },
    // The black slats over the fairing's engine window.
    { x0: 0.645, x1: 0.89, y0: 0.4, y1: 0.64 },
    // M153b: the fairing's upper facet. It runs from the base of the screen
    // down and back to the rider's knee, and it faces the sky, so it wears
    // the TOP painting (measured with the lab's view-debug card): a brow on
    // it was painted over by the top view's fire, a band across the skull.
    // Everything above its lower edge is no place for the eye or the brow.
    ...aboveLine(
      [[0.25, 0.08], [0.35, 0.105], [0.5, 0.162], [0.61, 0.218], [0.75, 0.295], [0.86, 0.36], [0.99, 0.415], [1.1, 0.45]],
      { overhang: true },
    ),
  ],
  sports: [
    // M153b: the mirror head (1.52-1.60 m back, 0.12 m outboard of the
    // flank, just above the belt). From the front three-quarter it drops
    // onto the flank just behind itself: over the M153 eye it sat on the
    // brow like a lump. The eye and the brow sit behind it, as at M144.
    { x0: 1.46, x1: 1.68, y0: -1, y1: 2, brow: true, overhang: true },
  ],
  f1: [
    // Open wheels: the tyres stand outboard of the body.
    { x0: 0.5, x1: 1.18, y0: -1, y1: 2 },
    { x0: 3.5, x1: 4.24, y0: -1, y1: 2 },
    // The mirror (1.94 m back, on the cockpit edge above the sidepod): from
    // behind and above, the chase angle, it drops over the flank ahead of
    // and below itself, where the M144 eye was.
    { x0: 1.7, x1: 2.06, y0: -1, y1: 0.28, overhang: true },
    // M153b: the front tyre seen from the front three-quarter. It stands
    // 0.24-0.51 m outboard of the cockpit's flank, so it hides the flank
    // behind it below this line (its rim projected along that view; the
    // M153 head's jaws and teeth were all behind it there).
    ...belowLine(
      [[1.0, 0.227], [1.1, 0.208], [1.2, 0.223], [1.3, 0.272], [1.4, 0.325], [1.5, 0.378], [1.6, 0.453], [1.66, 0.55], [1.69, 0.75]],
      { partial: true },
    ),
  ],
};

/** Is the side-view point (x, y) on paint the eye can see? */
function onMetal(fit: BodyFit, parts: Box[], x: number, y: number): boolean {
  if (x < 0 || x > fit.L || y < 0 || y > fit.H) return false;
  for (const b of parts) if (x >= b.x0 && x <= b.x1 && y >= b.y0 && y <= b.y1) return false;
  return flankCover(fit, x, x, Math.min(y, fit.H * 0.999), Math.min(y, fit.H * 0.999)) > 0.5;
}

/** The upper lip and the lower lip (head coordinates), from the head paths. */
const lipUp = (u: number): number => lerp(0.556, 0.616, clamp01(u / 0.63));
const lipLow = (u: number): number => lerp(0.885, 0.636, clamp01((u - 0.05) / 0.58));

/** The features that make the head read, as head-coordinate samples, each
 *  group with the share of it that must land on paint. A part over the eye,
 *  the brow, the snout or the jaws fails the head outright (HARD); the back
 *  of the skull only has to be mostly clear. */
interface Feature {
  pts: Pt[];
  need: number;
  weight: number;
  hard: boolean;
  /** The eye or the brow: a `brow` part must clear it, a `partial` one too. */
  eye?: boolean;
  /** The snout, the jaws, the lower jaw: what a `partial` part may hide some of. */
  face?: boolean;
}

const grid = (us: number[], ws: (u: number) => number[]): Pt[] => us.flatMap((u) => ws(u).map((w): Pt => [u, w]));

const FEATURES: Feature[] = [
  // The eye: the focal point. All of it.
  { pts: grid([0.43, 0.48, 0.53, 0.58, 0.62], () => [0.18, 0.24, 0.3, 0.36]), need: 0.95, weight: 4, hard: true, eye: true },
  // The brow over it.
  { pts: grid([0.42, 0.5, 0.58, 0.66, 0.72], () => [0.06, 0.13]), need: 0.9, weight: 2, hard: true, eye: true },
  // The snout (from the ridge down to the upper lip).
  { pts: grid([0.02, 0.1, 0.2, 0.3, 0.38], (u) => [(u < 0.17 ? 0.3 : lerp(0.3, 0.14, (u - 0.17) / 0.23)) + 0.04, 0.42, 0.5]), need: 0.93, weight: 1.5, hard: true, face: true },
  // The open jaws and the teeth.
  { pts: grid([0.06, 0.16, 0.28, 0.4, 0.52, 0.6], (u) => [lipUp(u) + 0.01, (lipUp(u) + lipLow(u)) / 2, lipLow(u) - 0.01]), need: 0.94, weight: 2, hard: true, face: true },
  // The lower jaw.
  { pts: grid([0.1, 0.25, 0.4, 0.55, 0.7, 0.85, 0.95], (u) => [Math.min(0.95, lipLow(u) + 0.05), 0.95]), need: 0.85, weight: 1.2, hard: true, face: true },
  // The cheek and the back of the skull.
  { pts: grid([0.66, 0.76, 0.86, 0.95], () => [0.22, 0.34, 0.46, 0.58]), need: 0.85, weight: 1, hard: false },
];

/**
 * M153b — only for a FITTED head (the M144 place is judged by FEATURES
 * alone, so the heads that kept it keep it): the very front of the
 * silhouette, the hooked beak and the tip of the chin, on paint. A fitted
 * head was free to slide its snout up to the arch, and the sports car's beak
 * and front fangs ran into the arch's edge, clipped. (The chin is left to
 * the lower jaw's own samples: on the monster truck's slanted arch a chin
 * rule cost the whole head an eighth of its size.)
 */
const FRONT_EDGE: Feature = {
  pts: [[-0.03, 0.36], [-0.03, 0.45], [-0.024, 0.54], [0.02, 0.62]],
  need: 1,
  weight: 1,
  hard: true,
  face: true,
};

/** M153b: the fire's gap ahead of the jaws (the red glow between the arch and
 *  the mouth that sells "breathing fire"): a fitted head that keeps it on
 *  paint is preferred, never required. */
const FIRE_GAP: Pt[] = grid([-0.16, -0.1], () => [0.5, 0.65, 0.8, 0.92]);

const inBox = (b: Box, x: number, y: number): boolean => x >= b.x0 && x <= b.x1 && y >= b.y0 && y <= b.y1;

/** How well a head sits: the weighted share of its features on paint, and
 *  whether every feature clears its own bar. `fitted` adds the checks a
 *  moved head must pass on top (the silhouette's front edge). */
function headFit(
  fit: BodyFit,
  parts: Box[],
  mouthX: number,
  headLen: number,
  headTop: number,
  headH: number,
  fitted = false,
): { pass: boolean; score: number; covered: number; hidden: number } {
  let sum = 0;
  let wsum = 0;
  let pass = true;
  // The share of the soft groups (the back of the skull) a part hangs over.
  let covered = 0;
  let soft = 0;
  // The share of the face a `partial` part hides from its one view.
  let hidden = 0;
  let face = 0;
  const shade = parts.filter((b) => b.partial);
  const eyeParts = parts.filter((b) => !b.partial);
  const bodyParts = eyeParts.filter((b) => !b.brow);
  const groups = fitted ? [...FEATURES, FRONT_EDGE] : FEATURES;
  const fail = { pass: false, score: 0, covered: 1, hidden: 1 };
  for (let i = 0; i < groups.length; i++) {
    const f = groups[i];
    const mine = f.eye ? eyeParts : bodyParts;
    let on = 0;
    for (const [u, w] of f.pts) {
      const x = mouthX + u * headLen;
      const y = headTop + w * headH;
      if (onMetal(fit, mine, x, y)) {
        on++;
        if (shade.length && shade.some((b) => inBox(b, x, y))) {
          // Behind an open wheel from the hero angle: never the eye.
          if (f.eye) return fail;
          hidden++;
        }
      } else if (mine.some((b) => inBox(b, x, y))) {
        if (f.hard) return fail;
        // A mirror or an arm over the back of the skull is tolerated (it
        // costs the placement a little, below): the skull is still there.
        on++;
        covered++;
      }
    }
    if (!f.hard) soft += f.pts.length;
    if (f.face) face += f.pts.length;
    const share = on / f.pts.length;
    // M153b: a fitted head's skull may tuck its last few centimetres into a
    // cavity behind it (the sports car's scoop lip): held to 85% it had to
    // shrink to two thirds of the M144 head to clear both scoop and mirror.
    if (share < (fitted && !f.hard ? Math.min(f.need, 0.75) : f.need)) {
      pass = false;
      // The eye decides first: a head whose eye is off the paint is not tried further.
      if (i === 0) return { pass, score: share * f.weight * 0.1, covered: 1, hidden: 1 };
    }
    sum += share * f.weight;
    wsum += f.weight;
  }
  const hiddenShare = face ? hidden / face : 0;
  // At least half the face must show from the hero angle.
  if (hiddenShare > 0.5) pass = false;
  return { pass, score: sum / wsum, covered: soft ? covered / soft : 0, hidden: hiddenShare };
}

/**
 * M153: how far behind the front arch the snout tip starts, per ride, in
 * metres. The default sits the jaws right behind the arch; a ride whose
 * fender carries a dark insert there needs the head moved back off it. The
 * coupe's M152 gill slits (carbon, never painted) cut straight through the
 * snout at the default — measured on the lab sheet, the slit crosses the
 * nose 0.1-0.25 m behind the arch — so its head starts past them, in the
 * fire, where a dark vent reads as a vent.
 */
const SNOUT_SHIFT: Record<string, number> = { coupe: 0.3 };

/** No limit on the frill and horns: the head sits where the M144 rule put it. */
const FULL_REACH = { frill: FRILL.map(() => Infinity), mid: Infinity, long: Infinity };

/** The M144 placement: the jaws just behind the front arch, the brow on the
 *  belt line, the chin on the sill. */
function naturalLayout(fit: BodyFit): Lay {
  const beltY = fit.H * (1 - fit.belt);
  const flank = fit.H - beltY;
  const front = fit.wheels[0];
  const rear = fit.wheels[fit.wheels.length - 1];
  // Enclosed arches (cars): the jaws open just behind the front arch.
  const enclosed = !!fit.cabin && !!front && front.y < fit.H + 0.7 * front.r;
  const mouthX = enclosed ? front.x + front.r * 1.2 + 0.02 + (SNOUT_SHIFT[fit.vehicle] ?? 0) : fit.L * 0.27;
  const back = enclosed && rear ? rear.x - rear.r * 1.05 : fit.L * 0.76;
  const headTop = beltY - flank * 0.04;
  const headH = (fit.H - headTop) / 1.045;
  // A deep, compact skull (2.3 heights long): longer than that it reads as a
  // crocodile on the car, however it is dressed.
  const headLen = Math.min(headH * 2.3, (back - mouthX) * 1.04);
  return {
    fit,
    beltY,
    mouthX,
    headLen,
    headTop,
    headH,
    cowl: fit.cabin?.x0 ?? fit.L * 0.27,
    tailGlass: fit.cabin?.x1 ?? fit.L * 0.7,
    reach: FULL_REACH,
    topFireTo: TOP_FIRE_TO[fit.vehicle] !== undefined ? mouthX + TOP_FIRE_TO[fit.vehicle] * headLen : Infinity,
  };
}

/**
 * Where the head sits on THIS body (M153). The M144 place is kept whenever
 * the eye, brow, snout, jaws and cheek all land on paint the eye can see
 * (the flank map: no scoop, window, arch or cut cavity under them; and no
 * part standing proud over them, PARTS). When they do not — the bike's head
 * on its engine window and under the rider's arm, the monster truck's jaws
 * round its side pipes, the Formula's cheek on the sidepod inlet, the sports
 * car's skull in its side scoop — the head is FITTED: the biggest head, kept
 * in proportion, that lands on metal, as near its natural place as it can
 * be. The frill and the swept horns then reach back only as far as the
 * flank behind them runs.
 */
function layout(fit: BodyFit): Lay {
  const nat = naturalLayout(fit);
  const parts = PARTS[fit.vehicle] ?? [];
  if ((!fit.flank && parts.length === 0) || headFit(fit, parts, nat.mouthX, nat.headLen, nat.headTop, nat.headH).pass) return nat;
  const front = fit.wheels[0];
  const enclosed = !!fit.cabin && !!front && front.y < fit.H + 0.7 * front.r;
  const x0 = enclosed ? front.x + front.r * 0.6 : 0;
  // (M153b: an open-wheeled body's head stays near its cockpit: given the
  // whole front half, the Formula's slid back onto the sidepod and its fire
  // then wrapped the entire cockpit.)
  const x1 = enclosed ? nat.mouthX + 0.8 : Math.min(fit.L * 0.55, nat.mouthX + 0.35);
  let best: { score: number; obj: number; mouthX: number; headLen: number; headTop: number; headH: number } | null = null;
  for (let s = 1; s >= 0.42; s -= 0.04) {
    const headH = nat.headH * s;
    // The head may drop down the flank (never climb above the belt), but its
    // chin stays on the body.
    const room = fit.H - nat.headTop - headH * 1.045;
    for (let t = 0; t <= Math.max(0, room) + 1e-6; t += 0.02) {
      const headTop = nat.headTop + t;
      for (const ratio of [2.3, 2.2, 2.1, 2, 1.9, 1.8]) {
        const headLen = headH * ratio;
        // Kept in proportion: a squatter head than the M144 skull pays for
        // it (a little: shorter reads as a dragon, longer as a crocodile).
        const shape = 1 - 0.3 * (2.3 - ratio);
        for (let mouthX = x0; mouthX <= x1; mouthX += 0.02) {
          const f = headFit(fit, parts, mouthX, headLen, headTop, headH, true);
          // The jaws belong just behind the front arch, breathing the fire
          // over it: a head far from there pays for the distance. M153b: one
          // that leaves paint for the fire's glow ahead of its jaws, and one
          // that shows its teeth from the hero angle, are preferred.
          let gap = 0;
          if (f.pass) for (const [u, w] of FIRE_GAP) if (onMetal(fit, parts, mouthX + u * headLen, headTop + w * headH)) gap++;
          const obj = f.pass
            ? headH * headLen * shape * Math.exp(-Math.abs(mouthX - nat.mouthX)) * (1 - 0.3 * f.covered) * (1 - 0.7 * f.hidden) * (0.8 + (0.2 * gap) / FIRE_GAP.length) - 0.05 * t
            : -1 + f.score;
          if (!best || obj > best.obj) best = { score: f.score, obj, mouthX, headLen, headTop, headH };
        }
      }
    }
  }
  if (!best) return nat;
  const { mouthX, headLen, headTop, headH } = best;
  // How far the flank behind the skull runs clear in a band of head
  // coordinates (w0..w1), in head lengths past the skull's back.
  const solid = parts.filter((b) => !b.overhang && !b.partial && !b.brow);
  const run = (w0: number, w1: number): number => {
    const back = mouthX + headLen;
    let x = mouthX + headLen * 0.96;
    for (; x < fit.L; x += 0.015) {
      let clear = true;
      for (let k = 0; k <= 3 && clear; k++) clear = onMetal(fit, solid, x, headTop + lerp(w0, w1, k / 3) * headH);
      if (!clear) break;
    }
    return Math.max(0, x - back) / headLen;
  };
  return {
    ...nat,
    mouthX,
    headLen,
    headTop,
    headH,
    reach: { frill: FRILL.map(([w, , lift]) => run(Math.min(w, w + lift) - 0.04, Math.max(w, w + lift) + 0.04)), mid: run(0.2, 0.32), long: run(-0.02, 0.13) },
    topFireTo: TOP_FIRE_TO[fit.vehicle] !== undefined ? mouthX + TOP_FIRE_TO[fit.vehicle] * headLen : Infinity,
  };
}

/** The head's place on a body (QA and tests: where the M153 fit put it). */
export function dragonHead(fit: BodyFit): { mouthX: number; headLen: number; headTop: number; headH: number; fitted: boolean } {
  const lay = layout(fit);
  return { mouthX: lay.mouthX, headLen: lay.headLen, headTop: lay.headTop, headH: lay.headH, fitted: lay.reach !== FULL_REACH };
}

/**
 * M153 — where the great roof horns root on a body whose roof is not where
 * the skull's back puts them (metres from the nose, and the share of the
 * half-width from the spine at the root and at the tip). The sports car's
 * dark side-window frames run down its roof to 2.69 m: horns rooted on the
 * roof and swept out over the haunches lost their middles under the frames
 * and read as cut plates, so its horns root behind the frames, on the
 * haunches.
 */
const GREAT_HORNS: Record<string, { x: number; across: [number, number] }> = {
  sports: { x: 2.74, across: [0.52, 0.86] },
};

/**
 * M153b — how deep the top view's flat tail edge runs, metres before the
 * tail (0.06 by default). The Formula's rear wing starts 0.1 m INSIDE the
 * measured length (4.33 m on a 4.44 m body) and reads the top view there.
 */
const TAIL_FLAT: Record<string, number> = { f1: 0.17 };

/**
 * M153b — where each ride's mirror head starts (metres from the nose,
 * measured off the models). The band above the belt that keeps the fire off
 * the mirror caps starts just ahead of it, not at the windscreen's base.
 * (The muscle car's band keeps the windscreen's base: its mirrors sit on
 * the screen frame.)
 */
const MIRROR_X: Record<string, number> = { coupe: 1.43, pickup: 1.5, sports: 1.52, van: 1.345 };

/** Pixels per metre of a view (its contexts are pre-scaled to metres). */
const pxm = (g: G): number => g.getTransform().a;

// ---------------------------------------------------------------------------
// FIRE. Heat is ACCUMULATED: a bed of embers and then a few dozen flame licks
// are laid additively into a scratch canvas (so heat piles up where licks
// cross), then every pixel's heat is mapped through a flame palette. That
// gives fire its real structure: red edges, an orange body, yellow cores,
// near-white only where it is densest.
// ---------------------------------------------------------------------------

/** Heat 0..1 → flame colour. Deep reds at the cold edge, orange through the
 *  body, yellow only where it is truly dense, near-white only at the core. */
const FLAME: [number, number, number, number][] = [
  [0.0, 16, 2, 0],
  [0.14, 104, 9, 0],
  [0.3, 198, 28, 0],
  [0.46, 244, 78, 0],
  [0.62, 255, 132, 4],
  [0.78, 255, 184, 28],
  [0.9, 255, 224, 88],
  [1.0, 255, 246, 184],
];

function flameColour(t: number, out: Uint8ClampedArray, i: number): void {
  let k = 0;
  while (k < FLAME.length - 2 && t > FLAME[k + 1][0]) k++;
  const a = FLAME[k];
  const b = FLAME[k + 1];
  const f = clamp01((t - a[0]) / (b[0] - a[0]));
  out[i] = a[1] + (b[1] - a[1]) * f;
  out[i + 1] = a[2] + (b[2] - a[2]) * f;
  out[i + 2] = a[3] + (b[3] - a[3]) * f;
}

interface Heat {
  /** The scratch context (scaled to metres like the view). Draw WHITE into
   *  it; it composites additively. */
  h: G;
  /**
   * One flame LICK: a tongue that swells behind its root and tapers to a
   * wavering point, laid as three nested layers (the whole lick, a shorter
   * narrower body, a short thin core). Heat adds up where they overlap, so
   * after the palette every lick has a red edge, an orange body and a yellow
   * core, the way a real flame is layered — and it keeps a SHAPE. (Measured
   * in the garage: one heavily blurred layer per lick read as blotchy
   * camouflage on the car, not as fire.)
   * `curl` turns it (radians) over its length.
   */
  wisp: (x: number, y: number, ang: number, len: number, width: number, heat: number, curl?: number) => void;
  /** A soft blob of heat (embers, the dense heart of a fire). */
  blob: (x: number, y: number, rx: number, ry: number, heat: number) => void;
  /** A bed of heat: `heat` up to (x0,y0), falling away to nothing at (x1,y1). */
  ramp: (x0: number, y0: number, x1: number, y1: number, heat: number) => void;
  /**
   * Map the accumulated heat to flame and composite it (and its glow).
   * `flow` stretches the turbulence into streaks: [along x, along y] noise
   * frequencies (low = long streaks that way).
   */
  commit: (gain?: number, flow?: [number, number]) => void;
}

function heatLayer(g: G, d: G | undefined, seed: number): Heat {
  // Fire is soft: its heat is accumulated at HALF the view's resolution (a
  // quarter of the pixels to blur and to map) and scaled up when committed.
  const HALF = 0.5;
  const w = Math.max(2, Math.round(g.canvas.width * HALF));
  const hgt = Math.max(2, Math.round(g.canvas.height * HALF));
  const c = document.createElement('canvas');
  c.width = w;
  c.height = hgt;
  const h = c.getContext('2d')!;
  const m = g.getTransform();
  h.setTransform(m.a * HALF, 0, 0, m.d * HALF, 0, 0);
  h.globalCompositeOperation = 'lighter';
  const k = pxm(g) * HALF;
  const rnd = rng(seed);
  // Headroom: everything is laid at HALF its heat and doubled when it is
  // read back. The 8-bit canvas clips at 1; without the headroom every place
  // two licks crossed clipped to the same flat yellow.
  const ROOM = 0.5;
  const wisp: Heat['wisp'] = (x, y, ang, len, width, heat, curl = 0) => {
    const n = 14;
    const ph = rnd() * 6.283;
    const fr = 0.6 + rnd() * 0.8;
    const wob = 0.36 + rnd() * 0.42;
    // The lick's spine: it wavers more the further it gets from its root.
    const spine: Pt[] = [];
    const dirs: number[] = [];
    let cx = x;
    let cy = y;
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const a = ang + curl * t + Math.sin(ph + t * fr * 6.283) * wob * t;
      if (i > 0) {
        cx += (Math.cos(a) * len) / n;
        cy += (Math.sin(a) * len) / n;
      }
      spine.push([cx, cy]);
      dirs.push(a);
    }
    // Layers are laid CRISP (a plain fill costs nothing; a blurred one costs
    // a millisecond, and there are hundreds): commit() softens the whole
    // layer once.
    const layer = (wide: number, long: number, a: number): void => {
      const m = Math.max(4, Math.round(n * long));
      const left: Pt[] = [];
      const right: Pt[] = [];
      for (let i = 0; i <= m; i++) {
        const t = i / m;
        // Fullest a fifth of the way up, then a long taper to a point.
        // (A rounded root: a blunt one stacks up into slabs.)
        const prof = t < 0.2 ? Math.sin((t / 0.2) * (Math.PI / 2)) : Math.pow(1 - (t - 0.2) / 0.8, 1.15);
        const half = (width / 2) * wide * prof;
        const [px, py] = spine[i];
        const dir = dirs[i];
        left.push([px - Math.sin(dir) * half, py + Math.cos(dir) * half]);
        right.push([px + Math.sin(dir) * half, py - Math.cos(dir) * half]);
      }
      h.fillStyle = `rgba(255,255,255,${Math.min(1, a * ROOM)})`;
      h.beginPath();
      h.moveTo(left[0][0], left[0][1]);
      for (let i = 1; i <= m; i++) h.lineTo(left[i][0], left[i][1]);
      for (let i = m; i >= 0; i--) h.lineTo(right[i][0], right[i][1]);
      h.closePath();
      h.fill();
    };
    layer(1, 1, heat * 0.55);
    layer(0.58, 0.8, heat * 0.5);
    layer(0.27, 0.56, heat * 0.55);
  };
  const ramp: Heat['ramp'] = (x0, y0, x1, y1, heat) => {
    const grad = h.createLinearGradient(x0, y0, x1, y1);
    grad.addColorStop(0, `rgba(255,255,255,${Math.min(1, heat * ROOM)})`);
    grad.addColorStop(0.55, `rgba(255,255,255,${Math.min(1, heat * ROOM) * 0.5})`);
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    h.fillStyle = grad;
    h.fillRect(-2, -2, 40, 40);
  };
  const blob: Heat['blob'] = (x, y, rx, ry, heat) => {
    h.save();
    h.translate(x, y);
    h.scale(rx, ry);
    const grad = h.createRadialGradient(0, 0, 0, 0, 0, 1);
    grad.addColorStop(0, `rgba(255,255,255,${heat * ROOM})`);
    grad.addColorStop(0.55, `rgba(255,255,255,${heat * ROOM * 0.5})`);
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    h.fillStyle = grad;
    h.fillRect(-1, -1, 2, 2);
    h.restore();
  };
  const commit: Heat['commit'] = (gain = 1, flow = [1.6, 4.5]) => {
    const src = h.getImageData(0, 0, w, hgt).data;
    // One blurred copy of everything laid so far. Mixed half and half with
    // the crisp original, every lick keeps its edge AND gets its soft bloom.
    // (A shape's blurred SHADOW, drawn with the shape itself pushed far
    // off-canvas, is the one soft-edge tool canvas 2D has everywhere, Safari
    // included: ctx.filter is not available there.)
    const softC = document.createElement('canvas');
    softC.width = w;
    softC.height = hgt;
    const sg = softC.getContext('2d')!;
    const off = w + 64;
    sg.shadowColor = '#fff';
    sg.shadowBlur = Math.max(2, 0.036 * k);
    sg.shadowOffsetX = off;
    sg.drawImage(c, -off, 0);
    const soft = sg.getImageData(0, 0, w, hgt).data;
    const img = new ImageData(w, hgt);
    const out = img.data;
    const glow = d ? new Uint8ClampedArray(w * hgt * 4) : null;
    for (let y = 0, i = 0; y < hgt; y++) {
      for (let x = 0; x < w; x++, i += 4) {
        // Heat lives in ALPHA: white laid additively on a clear canvas keeps
        // its colour at full white and piles up its coverage.
        const raw = ((src[i + 3] * 0.5 + soft[i + 3] * 0.5) / (255 * ROOM)) * gain;
        if (raw <= 0.01) continue;
        const mx = x / k;
        const my = y / k;
        // Turbulence stretched ALONG the flow: streaks, never blotches.
        const turb = 0.84 + 0.32 * fbm(mx * flow[0], my * flow[1], 3, seed + 5);
        // A soft shoulder: heat approaches 1 but never slams into it.
        const t = clamp01(1 - Math.exp(-raw * turb * 1.6));
        flameColour(t, out, i);
        out[i + 3] = 255 * smoothstep(0.03, 0.17, t);
        if (glow) {
          // Glow follows the heat; fire has no relief and a satin finish.
          glow[i] = 128;
          glow[i + 1] = 255 * Math.pow(t, 1.5);
          glow[i + 2] = 150;
          glow[i + 3] = 255 * smoothstep(0.05, 0.24, t);
        }
      }
    }
    const tmp = document.createElement('canvas');
    tmp.width = w;
    tmp.height = hgt;
    tmp.getContext('2d')!.putImageData(img, 0, 0);
    const W = g.canvas.width;
    const Hh = g.canvas.height;
    g.save();
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.imageSmoothingEnabled = true;
    g.imageSmoothingQuality = 'high';
    // The fire's own light on whatever is near it: a wide soft halo first…
    const small = document.createElement('canvas');
    small.width = Math.max(1, w >> 3);
    small.height = Math.max(1, hgt >> 3);
    small.getContext('2d')!.drawImage(tmp, 0, 0, small.width, small.height);
    g.globalCompositeOperation = 'lighter';
    g.globalAlpha = 0.3;
    g.drawImage(small, 0, 0, W, Hh);
    // …then the flames themselves.
    g.globalAlpha = 1;
    g.globalCompositeOperation = 'source-over';
    g.drawImage(tmp, 0, 0, W, Hh);
    g.restore();
    if (d && glow) {
      const gc = document.createElement('canvas');
      gc.width = w;
      gc.height = hgt;
      gc.getContext('2d')!.putImageData(new ImageData(glow, w, hgt), 0, 0);
      d.save();
      d.setTransform(1, 0, 0, 1, 0, 0);
      d.imageSmoothingEnabled = true;
      d.drawImage(gc, 0, 0, d.canvas.width, d.canvas.height);
      d.restore();
    }
  };
  return { h, wisp, blob, ramp, commit };
}

// ---------------------------------------------------------------------------
// Scales.
// ---------------------------------------------------------------------------

interface ScaleStyle {
  /** Scale length in metres at a point. */
  size: (x: number, y: number) => number;
  /** Which way the tips point (radians; 0 = toward the tail, +x). */
  dir?: (x: number, y: number) => number;
  /** Lightness shift −1..1 at a point (regional shading). */
  tone?: (x: number, y: number) => number;
  hue?: number;
  /** Keeled (a ridge down the middle): the big neck scales. */
  keel?: boolean;
  /** Tips point DOWN (+y) instead of toward the tail: the scales are laid
   *  bottom-up so each still tucks under the one above it. */
  down?: boolean;
}

const hsl = (h: number, s: number, l: number, a = 1): string =>
  `hsla(${h.toFixed(1)},${Math.max(0, Math.min(100, s)).toFixed(1)}%,${Math.max(0, Math.min(100, l)).toFixed(1)}%,${a})`;

/**
 * Lay overlapping scales over a metre rectangle, tail-first so every scale
 * tucks under the one ahead of it (the way a reptile's do). Draws the colour
 * and, when a data view is given, the matching relief.
 */
function scales(g: G, d: G | undefined, x0: number, y0: number, x1: number, y1: number, st: ScaleStyle, seed: number): void {
  const rnd = rng(seed);
  const hue = st.hue ?? 150;
  const down = st.down === true;
  // "Along" is the axis the tips point down (x toward the tail, or y when
  // the scales hang); "across" is the other one.
  const a0 = down ? y0 : x0;
  const a1 = down ? y1 : x1;
  const c0 = down ? x0 : y0;
  const c1 = down ? x1 : y1;
  let col = 0;
  for (let al = a1 + 0.2; al > a0 - 0.2; ) {
    const sx = down ? st.size((c0 + c1) / 2, al) : st.size(al, (c0 + c1) / 2);
    const stepY = sx * 0.8;
    const offs = col % 2 ? stepY / 2 : 0;
    for (let ac = c0 - stepY + offs; ac < c1 + stepY; ac += stepY) {
      const x = down ? ac : al;
      const y = down ? al : ac;
      const s = st.size(x, y) * (0.9 + rnd() * 0.2);
      const cx = x + (rnd() - 0.5) * s * 0.14;
      const cy = y + (rnd() - 0.5) * s * 0.14;
      const ang = (down ? Math.PI / 2 : 0) + (st.dir?.(cx, cy) ?? 0) + (rnd() - 0.5) * 0.2;
      // Mottling: broad dark bands and patches across the hide.
      const mottle = fbm(cx * 1.9, cy * 2.6, 3, seed + 3);
      const tone = (st.tone?.(cx, cy) ?? 0) + (mottle - 0.5) * 0.9;
      const v = rnd();
      const h = hue + (v - 0.5) * 16 + (mottle - 0.5) * 26;
      const sat = 58 + rnd() * 16;
      const lit = Math.max(3.5, 10.5 + v * 4.5 + tone * 9);
      const path = (c: G): void => {
        c.beginPath();
        c.moveTo(-0.46 * s, -0.37 * s);
        c.bezierCurveTo(0.08 * s, -0.54 * s, 0.56 * s, -0.31 * s, 0.6 * s, 0);
        c.bezierCurveTo(0.56 * s, 0.31 * s, 0.08 * s, 0.54 * s, -0.46 * s, 0.37 * s);
        c.closePath();
      };
      g.save();
      g.translate(cx, cy);
      g.rotate(ang);
      path(g);
      // Each scale is a little dome: dark where it tucks under its neighbour,
      // lit toward its free edge, with a soft sheen up and to the front.
      const body = g.createLinearGradient(-0.46 * s, -0.16 * s, 0.6 * s, 0.2 * s);
      body.addColorStop(0, hsl(h + 4, sat, lit * 0.3));
      body.addColorStop(0.55, hsl(h, sat, lit));
      body.addColorStop(1, hsl(h - 8, sat + 8, lit * 1.9 + 2));
      g.fillStyle = body;
      g.fill();
      const sheen = g.createRadialGradient(0.2 * s, -0.2 * s, 0, 0.2 * s, -0.2 * s, 0.34 * s);
      sheen.addColorStop(0, hsl(h - 22, 46, lit * 2.7 + 16, 0.5));
      sheen.addColorStop(1, hsl(h - 22, 46, lit * 2.7 + 16, 0));
      g.fillStyle = sheen;
      g.fill();
      // The free edge: a thin lit rim on top, the shadow it casts below.
      g.lineWidth = s * 0.05;
      g.strokeStyle = hsl(h - 16, sat - 8, lit * 2.4 + 14, 0.72);
      g.beginPath();
      g.moveTo(0.08 * s, -0.5 * s);
      g.bezierCurveTo(0.34 * s, -0.44 * s, 0.55 * s, -0.25 * s, 0.595 * s, -0.03 * s);
      g.stroke();
      g.strokeStyle = 'rgba(0,0,0,0.72)';
      g.lineWidth = s * 0.075;
      g.beginPath();
      g.moveTo(0.6 * s, 0.03 * s);
      g.bezierCurveTo(0.56 * s, 0.32 * s, 0.08 * s, 0.55 * s, -0.42 * s, 0.4 * s);
      g.stroke();
      if (st.keel) {
        g.strokeStyle = hsl(h - 12, sat - 6, lit * 2 + 8, 0.42);
        g.lineWidth = s * 0.04;
        g.beginPath();
        g.moveTo(-0.22 * s, 0);
        g.lineTo(0.52 * s, 0);
        g.stroke();
      }
      g.restore();
      if (d) {
        d.save();
        d.translate(cx, cy);
        d.rotate(ang);
        path(d);
        const rel = d.createLinearGradient(-0.46 * s, 0, 0.6 * s, 0);
        rel.addColorStop(0, 'rgb(40,0,92)');
        rel.addColorStop(0.7, 'rgb(168,0,64)');
        rel.addColorStop(1, 'rgb(226,0,52)');
        d.fillStyle = rel;
        d.fill();
        d.restore();
      }
    }
    al -= sx * 0.56;
    col++;
  }
}

/** A dark hide under the scales (it shows between them). */
function hide(g: G, d: G | undefined): void {
  g.fillStyle = '#030d07';
  g.fillRect(-1, -1, 60, 60);
  if (d) {
    d.fillStyle = 'rgb(26,0,124)';
    d.fillRect(-1, -1, 60, 60);
  }
}

// ---------------------------------------------------------------------------
// Shapes.
// ---------------------------------------------------------------------------

/** Trace a closed shape through points with smooth (quadratic) corners. */
function smoothPath(c: G | Path2D, pts: Pt[]): void {
  const n = pts.length;
  const mid = (a: Pt, b: Pt): Pt => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const start = mid(pts[n - 1], pts[0]);
  c.moveTo(start[0], start[1]);
  for (let i = 0; i < n; i++) {
    const p = pts[i];
    const m = mid(p, pts[(i + 1) % n]);
    c.quadraticCurveTo(p[0], p[1], m[0], m[1]);
  }
  c.closePath();
}

type Bone = { lit: string; mid: string; dark: string };
const IVORY: Bone = { lit: '#fff8e2', mid: '#e3d2a2', dark: '#7d6638' };
const HORN: Bone = { lit: '#e9dcb4', mid: '#9c8454', dark: '#33270f' };

/**
 * A horn / tooth / spine: a tapered, curved cone from a root to a tip, lit
 * down one flank, with growth rings when asked. `bend` bows it sideways.
 */
function spike(
  g: G,
  root: Pt,
  tip: Pt,
  width: number,
  bend: number,
  bone: Bone,
  d?: G,
  opts: { rings?: number; data?: string; shadow?: number; lit?: 1 | -1 } = {},
): void {
  const dx = tip[0] - root[0];
  const dy = tip[1] - root[1];
  const len = Math.hypot(dx, dy) || 1e-6;
  const nx = -dy / len;
  const ny = dx / len;
  const mx = (root[0] + tip[0]) / 2 + nx * bend;
  const my = (root[1] + tip[1]) / 2 + ny * bend;
  const shape = (c: G): void => {
    c.beginPath();
    c.moveTo(root[0] + (nx * width) / 2, root[1] + (ny * width) / 2);
    c.quadraticCurveTo(mx + nx * width * 0.3, my + ny * width * 0.3, tip[0], tip[1]);
    c.quadraticCurveTo(mx - nx * width * 0.3, my - ny * width * 0.3, root[0] - (nx * width) / 2, root[1] - (ny * width) / 2);
    c.closePath();
  };
  g.save();
  if (opts.shadow) {
    g.shadowColor = 'rgba(0,0,0,0.8)';
    g.shadowBlur = opts.shadow * pxm(g);
    g.shadowOffsetY = opts.shadow * pxm(g) * 0.4;
  }
  shape(g);
  // Lit along the upper flank, dark along the lower: reads as a cone.
  const up = opts.lit ?? (ny < 0 ? 1 : -1);
  const grad = g.createLinearGradient(
    mx + nx * width * 0.5 * up,
    my + ny * width * 0.5 * up,
    mx - nx * width * 0.5 * up,
    my - ny * width * 0.5 * up,
  );
  grad.addColorStop(0, bone.mid);
  grad.addColorStop(0.28, bone.lit);
  grad.addColorStop(0.62, bone.mid);
  grad.addColorStop(1, bone.dark);
  g.fillStyle = grad;
  g.fill();
  g.restore();
  g.save();
  shape(g);
  g.clip();
  // Darker toward the root, where it leaves the hide.
  const along = g.createLinearGradient(root[0], root[1], tip[0], tip[1]);
  along.addColorStop(0, 'rgba(20,12,2,0.6)');
  along.addColorStop(0.35, 'rgba(20,12,2,0)');
  g.fillStyle = along;
  g.fillRect(Math.min(root[0], tip[0]) - width, Math.min(root[1], tip[1]) - width, Math.abs(dx) + 2 * width, Math.abs(dy) + 2 * width);
  if (opts.rings) {
    g.strokeStyle = 'rgba(40,26,6,0.45)';
    g.lineWidth = width * 0.05;
    for (let i = 1; i <= opts.rings; i++) {
      const t = i / (opts.rings + 1.5);
      // Point on the bowed centreline, and the local half width.
      const px = (1 - t) * (1 - t) * root[0] + 2 * (1 - t) * t * mx + t * t * tip[0];
      const py = (1 - t) * (1 - t) * root[1] + 2 * (1 - t) * t * my + t * t * tip[1];
      const half = (width / 2) * (1 - t) * 1.15;
      g.beginPath();
      g.moveTo(px + nx * half, py + ny * half);
      g.quadraticCurveTo(px + (dx / len) * half * 0.5, py + (dy / len) * half * 0.5, px - nx * half, py - ny * half);
      g.stroke();
    }
  }
  g.restore();
  shape(g);
  g.strokeStyle = 'rgba(0,0,0,0.6)';
  g.lineWidth = Math.max(width * 0.05, 0.0025);
  g.stroke();
  if (d) {
    shape(d);
    d.fillStyle = opts.data ?? 'rgb(214,0,84)';
    d.fill();
  }
}

/** A soft glow hugging the INSIDE of an edge (`pts`), clipped by the caller:
 *  how rim light and contact shadow are painted. */
function edgeGlow(g: G, pts: Pt[], colour: string, blur: number, width: number): void {
  g.save();
  g.shadowColor = colour;
  g.shadowBlur = blur * pxm(g);
  g.strokeStyle = colour;
  g.lineWidth = width;
  g.lineJoin = 'round';
  g.lineCap = 'round';
  g.beginPath();
  pts.forEach(([x, y], i) => (i === 0 ? g.moveTo(x, y) : g.lineTo(x, y)));
  g.stroke();
  g.restore();
}

// ---------------------------------------------------------------------------
// The flank: neck, fire, head.
// ---------------------------------------------------------------------------

function paintSide(v: LiveryViews, dv: LiveryViews | undefined, lay: Lay): void {
  const g = v.side;
  const d = dv?.side;
  const { L, H } = lay.fit;
  const { mouthX, headLen, headTop, headH, beltY } = lay;
  const P = (u: number, w: number): Pt => [mouthX + u * headLen, headTop + w * headH];
  const hx1 = mouthX + headLen;
  const flank = H - beltY;

  // ---- 1. the neck: big keeled scales, lit along the shoulder, sinking
  // into shadow toward the sill and the tail.
  hide(g, d);
  scales(
    g,
    d,
    mouthX - 0.6,
    -0.1,
    L + 0.1,
    H + 0.1,
    {
      size: (x) => lerp(0.1, 0.15, smoothstep(hx1 - 0.3, L, x)),
      dir: (_x, y) => (y - (beltY + H) / 2) * 0.3,
      tone: (x, y) => 0.55 - smoothstep(beltY - 0.05, H, y) * 1.15 - 0.3 * smoothstep(hx1, L, x),
      keel: true,
    },
    SEED,
  );
  // A sheen along the shoulder (the top of the neck catches the sky).
  const shoulder = g.createLinearGradient(0, beltY - 0.02, 0, beltY + flank * 0.4);
  shoulder.addColorStop(0, 'rgba(150,255,200,0.16)');
  shoulder.addColorStop(1, 'rgba(150,255,200,0)');
  g.fillStyle = shoulder;
  g.fillRect(hx1 - 0.2, beltY - 0.02, L, flank * 0.4);
  // Belly plates along the sill: broad transverse scutes in old gold.
  const bellyTop = H - Math.min(0.105, flank * 0.17);
  for (let x = L + 0.1; x > mouthX + headLen * 0.82; x -= 0.085) {
    const y0 = bellyTop + Math.sin(x * 4.3) * 0.008;
    const grad = g.createLinearGradient(x - 0.085, 0, x + 0.012, 0);
    grad.addColorStop(0, '#191d08');
    grad.addColorStop(0.6, '#6f6a2c');
    grad.addColorStop(1, '#b3a85a');
    g.fillStyle = grad;
    g.beginPath();
    g.moveTo(x - 0.085, y0 + 0.004);
    g.quadraticCurveTo(x - 0.03, y0 - 0.014, x + 0.012, y0 + 0.008);
    g.lineTo(x + 0.012, H + 0.1);
    g.lineTo(x - 0.085, H + 0.1);
    g.closePath();
    g.fill();
    g.strokeStyle = 'rgba(4,8,2,0.85)';
    g.lineWidth = 0.007;
    g.stroke();
    if (d) {
      const rel = d.createLinearGradient(x - 0.085, 0, x + 0.012, 0);
      rel.addColorStop(0, 'rgb(56,0,88)');
      rel.addColorStop(1, 'rgb(200,0,64)');
      d.fillStyle = rel;
      d.fillRect(x - 0.085, y0, 0.097, H);
    }
  }
  const sill = g.createLinearGradient(0, H - flank * 0.28, 0, H);
  sill.addColorStop(0, 'rgba(0,0,0,0)');
  sill.addColorStop(1, 'rgba(0,0,0,0.5)');
  g.fillStyle = sill;
  g.fillRect(-1, H - flank * 0.28, L + 2, flank * 0.3);
  // Dorsal spines riding the shoulder line behind the skull, swept back.
  for (let x = L - 0.12, i = 0; x > hx1 + 0.28; x -= 0.19, i++) {
    const s = (0.15 + 0.035 * Math.sin(i * 1.9)) * lerp(1.1, 0.8, (x - hx1) / (L - hx1));
    spike(g, [x, beltY + 0.05], [x + s * 1.55, beltY - s * 0.5], s * 0.62, -s * 0.16, HORN, d, { shadow: 0.012, data: 'rgb(206,0,96)' });
  }

  // ---- 2. the scorch, then the fire pouring back off the nose.
  const yMouth = headTop + headH * 0.7;
  const scorch = g.createLinearGradient(mouthX - 0.3, 0, mouthX + headLen * 0.75, 0);
  scorch.addColorStop(0, 'rgba(5,2,1,0.97)');
  scorch.addColorStop(0.45, 'rgba(10,3,1,0.72)');
  scorch.addColorStop(1, 'rgba(10,3,1,0)');
  g.fillStyle = scorch;
  g.fillRect(-1, -1, mouthX + headLen * 0.75 + 1, H + 2);
  g.fillStyle = '#070201';
  g.fillRect(-1, -1, mouthX - 0.3 + 1, H + 2);
  {
    const fire = heatLayer(g, d, SEED + 3);
    const rnd = rng(SEED + 4);
    // A bed of embers under everything ahead of the lips, hottest at the
    // nose. It only sets the floor: the FLAMES are the licks laid over it.
    fire.ramp(0, 0, mouthX + 0.25, 0, 0.46);
    fire.blob(0, H * 0.55, mouthX * 0.7, H * 0.9, 0.3);
    // The licks. The car's own speed blows them back: over the snout they
    // lift toward the shoulder, under the chin they drag along the sill, and
    // level with the lips they stop short of the teeth.
    const area = (mouthX + 0.3) * H;
    const nBig = Math.round(11 * area) + 3;
    const nSmall = Math.round(17 * area) + 4;
    for (let i = 0; i < nBig + nSmall; i++) {
      const big = i < nBig;
      // Stratified down the flank: no bald bands, no pile-ups.
      const y = lerp(-0.06, H + 0.06, ((i % 8) + rnd()) / 8);
      const edge = clamp01(Math.abs(y - yMouth) / (headH * 0.52));
      // Roots anywhere in the fire; tips run past the lips only above the
      // snout and below the chin.
      const x = lerp(-0.3, mouthX * lerp(0.7, 1.0, smoothstep(0.35, 1, edge)), rnd());
      const len = (big ? lerp(0.55, 1.0, rnd()) : lerp(0.24, 0.48, rnd())) * lerp(0.7, 1.1, smoothstep(0.2, 1, edge));
      const width = big ? lerp(0.13, 0.23, rnd()) : lerp(0.06, 0.1, rnd());
      const up = y < yMouth ? -1 : 0.5;
      const ang = up * lerp(0.04, 0.24, rnd()) + (rnd() - 0.5) * 0.16;
      fire.wisp(x, y, ang, len, width, big ? 0.56 : 0.46, up * lerp(0.1, 0.5, rnd()));
    }
    fire.commit(0.88);
  }
  // M153: above the belt and behind the windscreen's base the fire's licks
  // only reached the pillars, the sail panels and the mirror caps (the rest is
  // glass): a cap in the fire lit up like an amber indicator lamp on its
  // stalk. That band is the neck again, scorched like the flank below it.
  // (Only as far back as the skull: the dorsal spines behind it ride the
  // shoulder line and keep their tips.)
  // M153b: where a ride's mirror is measured, the band starts just ahead of
  // the mirror head and fades in: the flame tip at the base of the A-pillar
  // (approved at M144, praised again on the pickup) keeps its fire.
  let bandX0 = Infinity;
  if (lay.fit.cabin) {
    const mirror = MIRROR_X[lay.fit.vehicle];
    const fade = mirror !== undefined ? 0.1 : 0;
    const x0 = mirror !== undefined ? Math.max(lay.fit.cabin.x0, mirror - 0.06 - fade) : lay.fit.cabin.x0;
    const x1 = Math.min(lay.fit.cabin.x1, hx1 + 0.2);
    bandX0 = x0 + fade / 2;
    // Painted on its own layer (same size and scale as the view), faded in
    // from x0, then laid over the view.
    const layer = (c: G): G => {
      const cv = document.createElement('canvas');
      cv.width = c.canvas.width;
      cv.height = c.canvas.height;
      const o = cv.getContext('2d')!;
      o.setTransform(c.getTransform());
      o.beginPath();
      o.rect(x0, -1, x1 - x0, 1 + beltY - 0.005);
      o.clip();
      return o;
    };
    const lg = layer(g);
    const ld = d ? layer(d) : undefined;
    hide(lg, ld);
    scales(
      lg,
      ld,
      x0 - 0.1,
      -0.1,
      x1 + 0.1,
      beltY + 0.05,
      {
        size: (x) => lerp(0.1, 0.15, smoothstep(hx1 - 0.3, L, x)),
        dir: (_x, y) => (y - (beltY + H) / 2) * 0.3,
        tone: (x, y) => 0.55 - smoothstep(beltY - 0.05, H, y) * 1.15 - 0.3 * smoothstep(hx1, L, x),
        keel: true,
      },
      SEED + 13,
    );
    const soot = lg.createLinearGradient(mouthX - 0.3, 0, mouthX + headLen * 0.75, 0);
    soot.addColorStop(0, 'rgba(5,2,1,0.97)');
    soot.addColorStop(0.45, 'rgba(10,3,1,0.72)');
    soot.addColorStop(1, 'rgba(10,3,1,0)');
    lg.fillStyle = soot;
    lg.fillRect(-1, -1, mouthX + headLen * 0.75 + 1, H + 2);
    for (const [o, c] of [[lg, g], ...(ld && d ? [[ld, d]] : [])] as [G, G][]) {
      if (fade > 0) {
        o.globalCompositeOperation = 'destination-in';
        const m = o.createLinearGradient(x0, 0, x0 + fade, 0);
        m.addColorStop(0, 'rgba(0,0,0,0)');
        m.addColorStop(1, 'rgba(0,0,0,1)');
        o.fillStyle = m;
        o.fillRect(-1, -1, L + 2, H + 2);
      }
      c.save();
      c.setTransform(1, 0, 0, 1, 0, 0);
      c.drawImage(o.canvas, 0, 0);
      c.restore();
    }
  }

  // ---- 3. the head.
  // The skull. Read from the nose: a hooked beak overhanging the lower jaw,
  // the nostril's bump, then a WEDGE climbing steadily to a heavy brow (a
  // long flat snout with the eyes perched behind it is a crocodile).
  const upper: Pt[] = [
    P(-0.012, 0.55), P(-0.024, 0.42), P(0.012, 0.33), P(0.075, 0.298), P(0.17, 0.3), P(0.29, 0.24),
    P(0.4, 0.14), P(0.52, 0.055), P(0.75, 0.035), P(0.93, 0.085), P(1.005, 0.23), P(0.99, 0.46),
    P(0.915, 0.6), P(0.775, 0.665), P(0.635, 0.615), P(0.48, 0.585), P(0.3, 0.56), P(0.15, 0.545),
    P(0.07, 0.572), P(0.022, 0.64),
  ];
  /** The snout's ridge line (height of the head's top edge at `u`). */
  const ridge = (u: number): number => (u < 0.17 ? 0.3 : lerp(0.3, 0.14, clamp01((u - 0.17) / 0.23)));
  const lower: Pt[] = [
    P(0.63, 0.63), P(0.46, 0.715), P(0.28, 0.8), P(0.11, 0.875), P(0.052, 0.885), P(0.032, 0.95),
    P(0.1, 1.02), P(0.32, 1.045), P(0.58, 1.03), P(0.82, 0.98), P(0.985, 0.84), P(1.0, 0.62),
    P(0.87, 0.6), P(0.74, 0.64),
  ];
  const mouth: Pt[] = [P(0.68, 0.625), P(0.45, 0.59), P(0.2, 0.555), P(0.01, 0.55), P(0.05, 0.885), P(0.24, 0.815), P(0.47, 0.71)];
  const headPath = new Path2D();
  smoothPath(headPath, upper);
  const jawPath = new Path2D();
  smoothPath(jawPath, lower);
  const mouthPath = new Path2D();
  smoothPath(mouthPath, mouth);

  // The neck falls into shadow behind the skull, and a deep soft shadow lifts
  // the head off it: from across the garage the head has to read as a SHAPE
  // before any of its detail does.
  const behind = g.createRadialGradient(...P(0.92, 0.5), headH * 0.2, ...P(0.92, 0.5), headLen * 0.85);
  behind.addColorStop(0, 'rgba(0,4,2,0.74)');
  behind.addColorStop(0.55, 'rgba(0,4,2,0.5)');
  behind.addColorStop(1, 'rgba(0,4,2,0)');
  g.fillStyle = behind;
  g.fillRect(mouthX, -0.2, headLen * 2, H + 0.4);
  g.save();
  g.shadowColor = 'rgba(0,0,0,0.95)';
  g.shadowBlur = 0.1 * pxm(g);
  g.fillStyle = '#020a05';
  g.fill(jawPath);
  g.fill(headPath);
  g.restore();

  // The throat: black at the hinge, glowing where the jet builds.
  g.save();
  g.clip(mouthPath);
  const throat = g.createLinearGradient(P(0.68, 0)[0], 0, P(0, 0)[0], 0);
  throat.addColorStop(0, '#0d0100');
  throat.addColorStop(0.45, '#3d0600');
  throat.addColorStop(1, '#7a1500');
  g.fillStyle = throat;
  g.fillRect(mouthX - 0.1, headTop, headLen, headH * 1.1);
  g.restore();
  if (d) {
    d.fillStyle = 'rgb(36,0,150)';
    d.fill(mouthPath);
  }

  // The hide, then the head's own small scales, clipped to each jaw.
  const dress = (path: Path2D, seed: number, jaw: boolean): void => {
    g.save();
    g.clip(path);
    const base = g.createLinearGradient(0, headTop, 0, headTop + headH);
    base.addColorStop(0, '#14442a');
    base.addColorStop(0.5, '#0b2a19');
    base.addColorStop(1, '#06180e');
    g.fillStyle = base;
    g.fillRect(mouthX - 0.2, headTop - 0.3, headLen + 0.5, headH + 0.6);
    if (d) {
      d.save();
      d.clip(path);
      d.fillStyle = 'rgb(84,0,110)';
      d.fillRect(mouthX - 0.2, headTop - 0.3, headLen + 0.5, headH + 0.6);
    }
    scales(
      g,
      d,
      mouthX - 0.1,
      headTop - 0.1,
      hx1 + 0.12,
      headTop + headH * 1.08,
      {
        size: (x, y) => {
          const u = (x - mouthX) / headLen;
          const w = (y - headTop) / headH;
          // Fine round the eye and along the lips, plated on the snout's
          // ridge, the cheek and the jaw.
          const eye = Math.hypot((u - 0.53) * 2.3, w - 0.25);
          const lip = jaw ? smoothstep(0.0, 0.16, w - (0.63 + (0.63 - u) * 0.42)) : smoothstep(0.0, 0.14, 0.6 - 0.07 * (1 - u) - w);
          return headH * lerp(0.062, 0.115, smoothstep(0.05, 0.95, u)) * lerp(0.55, 1.12, smoothstep(0.1, 0.55, eye)) * lerp(0.62, 1, lip);
        },
        dir: (x, y) => {
          const u = (x - mouthX) / headLen;
          const w = (y - headTop) / headH;
          return jaw ? -0.36 + (w - 0.85) * 0.5 : (w - 0.34) * 0.6 * (0.4 + u);
        },
        // The head is lit a little harder than the neck behind it (which is
        // how it stands off it) but stays a DARK green: the owner's brief, and
        // a bright head read as lime on the shop card.
        tone: (x, y) => {
          const u = (x - mouthX) / headLen;
          const w = (y - headTop) / headH;
          return jaw ? 0.2 - (w - 0.62) * 0.6 : 0.52 - w * 1.0 + 0.1 * (1 - u);
        },
        hue: jaw ? 136 : 144,
      },
      seed,
    );
    g.restore();
    d?.restore();
  };
  dress(jawPath, SEED + 7, true);
  dress(headPath, SEED + 5, false);

  // Nasal plates: a row of big shields down the ridge of the snout.
  g.save();
  g.clip(headPath);
  for (let i = 0; i < 8; i++) {
    const u = 0.045 + i * 0.047;
    const top = P(u, ridge(u) + 0.002);
    const s = headH * 0.13;
    g.save();
    g.translate(top[0] + s * 0.25, top[1] + s * 0.42);
    g.rotate(0.08);
    g.beginPath();
    g.moveTo(-0.5 * s, -0.4 * s);
    g.bezierCurveTo(0.1 * s, -0.56 * s, 0.56 * s, -0.3 * s, 0.6 * s, 0.05 * s);
    g.bezierCurveTo(0.5 * s, 0.34 * s, 0, 0.42 * s, -0.5 * s, 0.3 * s);
    g.closePath();
    const plate = g.createLinearGradient(0, -0.5 * s, 0, 0.4 * s);
    plate.addColorStop(0, '#2f7a4f');
    plate.addColorStop(0.3, '#1a5334');
    plate.addColorStop(1, '#04140b');
    g.fillStyle = plate;
    g.fill();
    g.strokeStyle = 'rgba(0,0,0,0.75)';
    g.lineWidth = s * 0.07;
    g.stroke();
    g.restore();
  }
  g.restore();

  // Lip scutes: squared plates lining both lips.
  const lipRow = (from: Pt, ctrl: Pt, to: Pt, n: number, hgt: number, path: Path2D, down: boolean): void => {
    g.save();
    g.clip(path);
    for (let i = 0; i < n; i++) {
      const q = (t: number): Pt => [
        (1 - t) * (1 - t) * from[0] + 2 * (1 - t) * t * ctrl[0] + t * t * to[0],
        (1 - t) * (1 - t) * from[1] + 2 * (1 - t) * t * ctrl[1] + t * t * to[1],
      ];
      const a = q(i / n);
      const b = q((i + 0.9) / n);
      const k = down ? -1 : 1;
      g.beginPath();
      g.moveTo(a[0], a[1]);
      g.lineTo(b[0], b[1]);
      g.lineTo(b[0] + hgt * 0.1, b[1] + k * hgt);
      g.quadraticCurveTo((a[0] + b[0]) / 2, a[1] + k * hgt * 1.25, a[0] + hgt * 0.1, a[1] + k * hgt);
      g.closePath();
      const grad = g.createLinearGradient(0, a[1], 0, a[1] + k * hgt);
      grad.addColorStop(0, down ? '#08221a' : '#236640');
      grad.addColorStop(1, down ? '#236640' : '#08221a');
      g.fillStyle = grad;
      g.fill();
      g.strokeStyle = 'rgba(0,0,0,0.7)';
      g.lineWidth = hgt * 0.1;
      g.stroke();
    }
    g.restore();
  };
  lipRow(P(0.075, 0.566), P(0.3, 0.548), P(0.62, 0.612), 13, headH * 0.062, headPath, true);
  lipRow(P(0.075, 0.885), P(0.32, 0.79), P(0.61, 0.642), 13, headH * 0.06, jawPath, false);

  // Form. The skull is a solid thing: sky light along its top line, a shelf
  // of shadow under the brow, the cheek swelling, the jaw falling away.
  g.save();
  g.clip(headPath);
  edgeGlow(g, [P(0.0, 0.34), P(0.075, 0.298), P(0.17, 0.3), P(0.29, 0.24), P(0.4, 0.14), P(0.52, 0.055), P(0.76, 0.03), P(0.94, 0.08)], 'rgba(170,255,205,0.5)', 0.035, 0.012);
  const cheekLight = g.createRadialGradient(...P(0.8, 0.36), 0, ...P(0.8, 0.36), headLen * 0.2);
  cheekLight.addColorStop(0, 'rgba(120,235,160,0.2)');
  cheekLight.addColorStop(1, 'rgba(120,235,160,0)');
  g.fillStyle = cheekLight;
  g.fillRect(mouthX, headTop, headLen * 1.1, headH);
  // Shadow pooling under the brow shelf and behind the cheek.
  const browShade = g.createRadialGradient(...P(0.53, 0.26), 0, ...P(0.53, 0.26), headLen * 0.16);
  browShade.addColorStop(0, 'rgba(0,0,0,0.78)');
  browShade.addColorStop(1, 'rgba(0,0,0,0)');
  g.fillStyle = browShade;
  g.fillRect(mouthX, headTop, headLen * 1.1, headH);
  edgeGlow(g, [P(0.63, 0.62), P(0.775, 0.67), P(0.915, 0.605), P(0.99, 0.46)], 'rgba(0,0,0,0.85)', 0.04, 0.02);
  g.restore();
  g.save();
  g.clip(jawPath);
  edgeGlow(g, [P(0.035, 0.95), P(0.1, 1.02), P(0.32, 1.045), P(0.58, 1.03), P(0.82, 0.98), P(0.985, 0.84)], 'rgba(0,0,0,0.9)', 0.05, 0.03);
  g.restore();

  // ---- 4. the jet: white-hot in the throat, fanning out past the teeth.
  g.save();
  g.clip(mouthPath);
  d?.save();
  d?.clip(mouthPath);
  {
    const jet = heatLayer(g, d, SEED + 9);
    const rnd = rng(SEED + 10);
    const throatPt = P(0.62, 0.635);
    for (let i = 0; i < 14; i++) {
      const t = rnd();
      const spread = lerp(-1, 1, rnd());
      const start: Pt = [throatPt[0] - t * headLen * 0.3, throatPt[1] + spread * headH * 0.02 * (1 + t * 4)];
      // Aimed at the gap between the lips, opening out as it goes.
      jet.wisp(start[0], start[1], Math.PI + 0.1 + spread * 0.16, headLen * lerp(0.3, 0.62, rnd()), headH * lerp(0.06, 0.13, rnd()), 0.6, spread * 0.25);
    }
    jet.blob(...P(0.44, 0.655), headLen * 0.3, headH * 0.055, 0.7);
    jet.blob(...P(0.14, 0.71), headLen * 0.2, headH * 0.14, 0.2);
    jet.commit(1.1);
  }
  g.restore();
  d?.restore();

  // ---- 5. the fire lights the face from in front and below: a hot rim on
  // both lips, the chin and the underside of the snout.
  for (const [path, edge] of [
    [headPath, [P(0.022, 0.64), P(0.07, 0.572), P(0.15, 0.545), P(0.3, 0.56), P(0.48, 0.585), P(0.63, 0.615)]],
    [jawPath, [P(0.63, 0.63), P(0.46, 0.715), P(0.28, 0.8), P(0.11, 0.875), P(0.04, 0.9)]],
    [headPath, [P(-0.024, 0.42), P(-0.012, 0.55), P(0.022, 0.64)]],
    [jawPath, [P(0.045, 0.89), P(0.032, 0.95), P(0.1, 1.02)]],
  ] as [Path2D, Pt[]][]) {
    g.save();
    g.clip(path);
    g.globalCompositeOperation = 'lighter';
    edgeGlow(g, edge, 'rgba(255,120,20,0.85)', 0.05, 0.016);
    edgeGlow(g, edge, 'rgba(255,200,90,0.6)', 0.014, 0.006);
    g.restore();
  }
  for (const path of [headPath, jawPath]) {
    g.save();
    g.clip(path);
    g.globalCompositeOperation = 'lighter';
    const glow = g.createRadialGradient(...P(0.08, 0.7), headH * 0.05, ...P(0.08, 0.7), headLen * 0.52);
    glow.addColorStop(0, 'rgba(255,110,14,0.4)');
    glow.addColorStop(0.5, 'rgba(255,80,6,0.16)');
    glow.addColorStop(1, 'rgba(255,60,0,0)');
    g.fillStyle = glow;
    g.fillRect(mouthX - 0.2, headTop - 0.2, headLen + 0.4, headH * 1.3);
    g.restore();
  }

  // ---- 6. teeth, bright against the fire.
  const tooth = (u: number, w: number, len: number, up: boolean, lean: number): void => {
    const root = P(u, w);
    const tip = P(u - lean, w + (up ? -len : len));
    spike(g, root, tip, headLen * (0.026 + len * 0.15), headLen * 0.004 * (up ? 1 : -1), IVORY, d, { data: 'rgb(236,0,40)' });
  };
  // Few and BIG: a fang up front in each jaw, then a ragged row falling away
  // to the hinge. (A fine even row reads as a zip from any distance.)
  const upperTeeth: [number, number, number][] = [
    [0.1, 0.56, 0.3], [0.17, 0.55, 0.13], [0.24, 0.556, 0.2], [0.31, 0.566, 0.11],
    [0.375, 0.576, 0.15], [0.445, 0.586, 0.085], [0.51, 0.596, 0.095], [0.57, 0.607, 0.05],
  ];
  const lowerTeeth: [number, number, number][] = [
    [0.08, 0.88, 0.25], [0.145, 0.853, 0.11], [0.21, 0.826, 0.17], [0.28, 0.797, 0.095], [0.35, 0.765, 0.13],
    [0.42, 0.732, 0.075], [0.49, 0.699, 0.07],
  ];
  for (const [u, w, len] of lowerTeeth) tooth(u, w, len, true, 0.012);
  for (const [u, w, len] of upperTeeth) tooth(u, w, len, false, 0.014);
  // Gums.
  g.lineWidth = headH * 0.028;
  g.lineCap = 'round';
  g.strokeStyle = '#2a0503';
  g.beginPath();
  g.moveTo(...P(0.075, 0.57));
  g.quadraticCurveTo(...P(0.3, 0.548), ...P(0.6, 0.612));
  g.stroke();
  g.beginPath();
  g.moveTo(...P(0.055, 0.888));
  g.quadraticCurveTo(...P(0.3, 0.79), ...P(0.58, 0.655));
  g.stroke();

  // ---- 7. the brow, the eye, the nostril.
  // The eye is the focal point of the whole wrap: sized to be found from
  // across the garage (and on a phone), not to anatomy.
  const eyeC = P(0.525, 0.285);
  const ew = headLen * 0.1;
  const eh = headH * 0.19;
  // The eye lights its own socket.
  g.save();
  g.globalCompositeOperation = 'lighter';
  const halo = g.createRadialGradient(eyeC[0], eyeC[1], 0, eyeC[0], eyeC[1], ew * 2.4);
  halo.addColorStop(0, 'rgba(255,200,40,0.62)');
  halo.addColorStop(0.5, 'rgba(255,160,10,0.22)');
  halo.addColorStop(1, 'rgba(255,150,0,0)');
  g.fillStyle = halo;
  g.fillRect(eyeC[0] - ew * 2.4, eyeC[1] - ew * 2.4, ew * 4.8, ew * 4.8);
  g.restore();
  // The eyeball: an almond slanted down toward the snout (it glares).
  const almond = (c: G): void => {
    c.beginPath();
    c.moveTo(eyeC[0] - ew * 1.05, eyeC[1] + eh * 0.62);
    c.quadraticCurveTo(eyeC[0] - ew * 0.1, eyeC[1] - eh * 1.3, eyeC[0] + ew, eyeC[1] - eh * 0.62);
    c.quadraticCurveTo(eyeC[0] + ew * 0.3, eyeC[1] + eh * 1.05, eyeC[0] - ew * 1.05, eyeC[1] + eh * 0.62);
    c.closePath();
  };
  g.save();
  almond(g);
  g.clip();
  const iris = g.createRadialGradient(eyeC[0], eyeC[1], 0, eyeC[0], eyeC[1], ew * 1.05);
  iris.addColorStop(0, '#fff6a8');
  iris.addColorStop(0.22, '#ffe020');
  iris.addColorStop(0.6, '#ffa900');
  iris.addColorStop(1, '#8a3d00');
  g.fillStyle = iris;
  g.fillRect(eyeC[0] - ew * 1.3, eyeC[1] - eh * 1.5, ew * 2.6, eh * 3);
  g.strokeStyle = 'rgba(140,60,0,0.5)';
  g.lineWidth = ew * 0.028;
  for (let i = 0; i < 34; i++) {
    const a = (i / 34) * Math.PI * 2;
    g.beginPath();
    g.moveTo(eyeC[0] + Math.cos(a) * ew * 0.18, eyeC[1] + Math.sin(a) * eh * 0.3);
    g.lineTo(eyeC[0] + Math.cos(a) * ew * 1.1, eyeC[1] + Math.sin(a) * eh * 1.3);
    g.stroke();
  }
  g.fillStyle = '#040201';
  g.beginPath();
  g.ellipse(eyeC[0], eyeC[1] - eh * 0.04, ew * 0.15, eh * 0.9, -0.16, 0, Math.PI * 2);
  g.fill();
  // Only the brow's own shadow falls across it: the iris stays lit.
  const lid = g.createLinearGradient(0, eyeC[1] - eh * 1.2, 0, eyeC[1] - eh * 0.35);
  lid.addColorStop(0, 'rgba(0,0,0,0.8)');
  lid.addColorStop(1, 'rgba(0,0,0,0)');
  g.fillStyle = lid;
  g.fillRect(eyeC[0] - ew * 1.3, eyeC[1] - eh * 1.5, ew * 2.6, eh * 1.7);
  g.fillStyle = 'rgba(255,255,255,0.96)';
  g.beginPath();
  g.ellipse(eyeC[0] - ew * 0.36, eyeC[1] - eh * 0.22, ew * 0.1, eh * 0.13, -0.5, 0, Math.PI * 2);
  g.fill();
  g.restore();
  almond(g);
  g.strokeStyle = '#010302';
  g.lineWidth = ew * 0.12;
  g.stroke();
  if (d) {
    almond(d);
    d.fillStyle = 'rgb(214,205,12)'; // a raised, glowing, wet eye
    d.fill();
  }
  // The brow: a heavy shelf of horn-plate overhanging the eye.
  const brow: Pt[] = [P(0.375, 0.245), P(0.43, 0.14), P(0.54, 0.07), P(0.68, 0.085), P(0.735, 0.15), P(0.64, 0.158), P(0.53, 0.175), P(0.44, 0.235)];
  g.save();
  g.shadowColor = 'rgba(0,0,0,0.95)';
  g.shadowBlur = 0.02 * pxm(g);
  g.shadowOffsetY = 0.008 * pxm(g);
  g.beginPath();
  smoothPath(g, brow);
  const browFill = g.createLinearGradient(0, P(0, 0.06)[1], 0, P(0, 0.24)[1]);
  browFill.addColorStop(0, '#4c9a6c');
  browFill.addColorStop(0.3, '#174a2e');
  browFill.addColorStop(1, '#020c07');
  g.fillStyle = browFill;
  g.fill();
  g.restore();
  if (d) {
    d.beginPath();
    smoothPath(d, brow);
    d.fillStyle = 'rgb(238,0,64)';
    d.fill();
  }
  // Nostril: a dark comma with a lit rim, breathing a wisp of ember light.
  const nos = P(0.045, 0.37);
  g.fillStyle = '#010302';
  g.beginPath();
  g.ellipse(nos[0], nos[1], headLen * 0.022, headH * 0.03, -0.6, 0, Math.PI * 2);
  g.fill();
  g.strokeStyle = 'rgba(140,235,170,0.55)';
  g.lineWidth = headH * 0.012;
  g.beginPath();
  g.ellipse(nos[0], nos[1], headLen * 0.03, headH * 0.042, -0.6, Math.PI * 1.02, Math.PI * 1.95);
  g.stroke();

  // ---- 8. horns and spines, swept back along the shoulder.
  const sh = 0.014;
  // The frill: four spines fanning back off the cheek with a web of skin
  // stretched between them (the one feature no crocodile has).
  // M153: on a fitted head they reach back only as far as the flank runs
  // (a scoop, an inlet or a pipe right behind the skull cuts them short).
  // M153b: each spine is cut by what is behind it at its own height, and a
  // spine left too short to read as a spine is not drawn at all (a row of
  // short stubs read as a second set of teeth): the web then hugs the cheek.
  const frill: [number, number, number][] = FRILL.map(([w, len, lift], i) => {
    const k = Math.min(1, (lay.reach.frill[i] + 0.025) / len);
    return k * len < 0.13 ? [w, 0, 0] : [w, len * k, lift * k];
  });
  const frillTip = (i: number): Pt => P(0.975 + frill[i][1] * 0.9, frill[i][0] + frill[i][2]);
  const web = (c: G): void => {
    c.beginPath();
    c.moveTo(...P(0.94, 0.4));
    c.lineTo(...frillTip(0));
    for (let i = 1; i < frill.length; i++) {
      const a = frillTip(i - 1);
      const b = frillTip(i);
      // Scalloped between the spines, the way stretched skin hangs.
      c.quadraticCurveTo((a[0] + b[0]) / 2 - headLen * 0.085, (a[1] + b[1]) / 2, b[0], b[1]);
    }
    c.lineTo(...P(0.94, 0.9));
    c.closePath();
  };
  const anyFrill = frill.some(([, len]) => len > 0);
  if (anyFrill) {
    g.save();
    g.shadowColor = 'rgba(0,0,0,0.85)';
    g.shadowBlur = 0.03 * pxm(g);
    web(g);
    const skin = g.createLinearGradient(P(0.94, 0)[0], 0, P(1.3, 0)[0], 0);
    skin.addColorStop(0, '#04180e');
    skin.addColorStop(0.5, '#12482c');
    skin.addColorStop(1, '#2b7a4d');
    g.fillStyle = skin;
    g.fill();
    g.restore();
    g.save();
    web(g);
    g.clip();
    // Veins run out along the skin between the spines.
    g.strokeStyle = 'rgba(150,235,180,0.3)';
    g.lineWidth = headH * 0.009;
    for (let i = 0; i < 9; i++) {
      const w = lerp(0.46, 0.88, i / 8);
      g.beginPath();
      g.moveTo(...P(0.95, w));
      g.quadraticCurveTo(...P(1.08, w + 0.01), ...P(1.26, w + (w - 0.66) * 0.12));
      g.stroke();
    }
    g.restore();
    // Its free edge catches the light.
    g.save();
    g.strokeStyle = 'rgba(170,245,200,0.5)';
    g.lineWidth = headH * 0.012;
    g.lineJoin = 'round';
    g.beginPath();
    g.moveTo(...frillTip(0));
    for (let i = 1; i < frill.length; i++) {
      const a = frillTip(i - 1);
      const b = frillTip(i);
      g.quadraticCurveTo((a[0] + b[0]) / 2 - headLen * 0.085, (a[1] + b[1]) / 2, b[0], b[1]);
    }
    g.stroke();
    g.restore();
    if (d) {
      web(d);
      d.fillStyle = 'rgb(112,0,104)';
      d.fill();
    }
  }
  for (const [w, len, lift] of frill) {
    if (len <= 0) continue;
    spike(g, P(0.975, w), P(0.975 + len, w + lift), headH * 0.1, headH * 0.03, HORN, d, { shadow: sh, rings: 2 });
  }
  // The two great horns, swept back along the shoulder (shortened, like the
  // frill, where the flank behind a fitted head runs out).
  const horn = (root: Pt, tip: Pt, reach: number, width: number, bend: number, rings: number): void => {
    const k = Math.min(1, Math.max(0.3, (1 + reach - root[0]) / (tip[0] - root[0])));
    const end: Pt = k >= 1 ? tip : [lerp(root[0], tip[0], k), lerp(root[1], tip[1], k)];
    spike(g, P(...root), P(...end), width * lerp(0.75, 1, k), bend * k, HORN, d, { shadow: sh, rings: Math.max(2, Math.round(rings * k)) });
  };
  horn([0.955, 0.295], [1.43, 0.215], lay.reach.mid, headH * 0.165, -headH * 0.07, 5);
  horn([0.87, 0.115], [1.58, -0.02], lay.reach.long, headH * 0.235, -headH * 0.14, 8);
  // Brow spines and the horn on the nose.
  for (const [u, w, len] of [[0.67, 0.09, 0.2], [0.59, 0.065, 0.15], [0.5, 0.075, 0.1]] as [number, number, number][]) {
    spike(g, P(u, w), P(u + len * 0.95, w - len * 0.42), headH * 0.085, -headH * 0.02, HORN, d, { shadow: 0.008 });
  }
  spike(g, P(0.04, 0.315), P(0.105, 0.07), headH * 0.115, headH * 0.03, HORN, d, { shadow: 0.008, rings: 2 });
  // Chin barbs.
  for (const [u, len] of [[0.16, 0.1], [0.3, 0.085]] as [number, number][]) {
    spike(g, P(u, 1.03), P(u + 0.07, 1.03 + len), headH * 0.06, 0, HORN, d);
  }

  // ---- 9. embers torn off the fire, drifting back down the flank.
  const rnd = rng(SEED + 21);
  for (let i = 0; i < 70; i++) {
    const x = lerp(mouthX * 0.7, hx1 + 0.7, Math.pow(rnd(), 1.5));
    const y = rnd() < 0.55 ? lerp(-0.02, beltY + flank * 0.22, rnd()) : lerp(H - flank * 0.3, H, rnd());
    const r = 0.0035 + rnd() * 0.007;
    const len = r * (2 + rnd() * 5);
    g.save();
    g.globalCompositeOperation = 'lighter';
    const hot = g.createLinearGradient(x, y, x + len, y - len * 0.2);
    hot.addColorStop(0, 'rgba(255,225,140,0.95)');
    hot.addColorStop(0.4, 'rgba(255,120,10,0.6)');
    hot.addColorStop(1, 'rgba(255,60,0,0)');
    g.strokeStyle = hot;
    g.lineWidth = r * 1.5;
    g.lineCap = 'round';
    g.beginPath();
    g.moveTo(x, y);
    g.lineTo(x + len, y - len * 0.2);
    g.stroke();
    g.restore();
    if (d) {
      d.fillStyle = 'rgba(128,255,150,0.9)';
      d.beginPath();
      d.arc(x, y, r * 1.5, 0, Math.PI * 2);
      d.fill();
    }
  }

  // ---- 10. M153: nothing above the belt behind the windscreen's base GLOWS.
  // Only the pillars and the mirror caps read the painting up there (the rest
  // is glass), and a mirror cap in the fire's glow lit up like an indicator
  // lamp. The colour stays; only the glow channel is taken out.
  if (d && lay.fit.cabin) {
    d.save();
    d.globalCompositeOperation = 'multiply';
    const fade = d.createLinearGradient(0, beltY - 0.05, 0, beltY + 0.01);
    fade.addColorStop(0, 'rgba(255,0,255,1)');
    fade.addColorStop(1, 'rgba(255,0,255,0)');
    d.fillStyle = fade;
    d.fillRect(bandX0, -1, L + 1, beltY + 1.01);
    d.restore();
  }

  // ---- 11. M153: wheels hung BELOW the body (the monster truck's) have
  // painted parts that read the livery off the body's bottom edge: its
  // beadlock rings wore a slice of the fire in front and the murk of the sill
  // behind, so no two wheels matched. Under such a wheel the edge is one tone,
  // the belly plates' old gold.
  for (const wh of lay.fit.wheels) {
    if (wh.y - wh.r * 0.5 <= H) continue;
    g.fillStyle = '#8a7634';
    g.fillRect(wh.x - wh.r * 1.15, H - 0.012, wh.r * 2.3, 0.2);
    if (d) {
      d.fillStyle = 'rgb(70,0,96)';
      d.fillRect(wh.x - wh.r * 1.15, H - 0.012, wh.r * 2.3, 0.2);
    }
  }
}

// ---------------------------------------------------------------------------
// Top view: fire over the hood, the skull on the roof, the neck on the deck.
// ---------------------------------------------------------------------------

function paintTop(v: LiveryViews, dv: LiveryViews | undefined, lay: Lay): void {
  const g = v.top;
  const d = dv?.top;
  const { L, W } = lay.fit;
  const mid = W / 2;
  const skull0 = lay.cowl;
  const skull1 = lay.tailGlass;
  hide(g, d);
  // Scales fan away from the spine and grow toward the tail.
  scales(
    g,
    d,
    skull0 - 0.7,
    -0.1,
    L + 0.1,
    W + 0.1,
    {
      // Armour: the plates are biggest along the spine and toward the tail.
      size: (x, y) => lerp(0.085, 0.15, smoothstep(skull0, L, x)) * lerp(1.35, 0.85, clamp01(Math.abs(y - mid) / (mid * 0.7))),
      dir: (_x, y) => ((y - mid) / mid) * 0.55,
      tone: (x, y) => 0.6 - Math.abs((y - mid) / mid) * 0.95 - 0.2 * smoothstep(skull1, L, x),
      keel: true,
    },
    SEED + 31,
  );
  // How deep the flat tail edge runs (see below).
  const tail = TAIL_FLAT[lay.fit.vehicle] ?? 0.06;
  // The spine: a row of dorsal plates from the crown to the tail.
  for (let x = L - 0.08; x > skull0 + 0.02; x -= 0.2) {
    const s = 0.2 + 0.03 * Math.sin(x * 4.1);
    // M153: none reaches the tail edge (see the flat edge below); the rear
    // view carries the spine on down the tail.
    if (x + s * 0.75 > L - tail - 0.07) continue;
    spike(g, [x - s * 0.5, mid], [x + s * 0.75, mid], s * 0.34, 0, HORN, d, { shadow: 0.008, data: 'rgb(244,0,80)' });
  }
  // Horns: rooted at the back of the skull, sweeping out over the deck.
  const great = GREAT_HORNS[lay.fit.vehicle];
  for (const s of [-1, 1]) {
    const y = (f: number): number => mid + s * f * mid;
    const root: Pt = [Math.min(skull1 - 0.15, skull0 + 0.95), y(0.34)];
    const lit = s < 0 ? 1 : -1; // both horns lit along their inner flank
    spike(g, [root[0] - 0.25, y(0.66)], [Math.min(L - 0.2, root[0] + 0.5), y(0.9)], 0.11, s * 0.05, HORN, d, { shadow: 0.012, rings: 4, lit });
    const gRoot: Pt = great ? [great.x, y(great.across[0])] : root;
    const gTip: Pt = great ? [Math.min(L - tail, great.x + 1.15), y(great.across[1])] : [Math.min(L - tail, root[0] + 1.15), y(0.74)];
    spike(g, gRoot, gTip, 0.2, s * 0.12, HORN, d, { shadow: 0.014, rings: 8, lit });
  }
  // M153: the tail edge itself is one flat dark tone. A bolt-on behind the
  // measured body (the Formula's rear wing) reads the livery off this edge,
  // stretched over its whole chord: scales or a spine plate there became long
  // green streaks with a bone bar down the middle. (M153b: on the Formula
  // the wing's leading edge sits INSIDE the measured length, over the last
  // 0.1 m, and still wore a row of chopped scales: its flat edge runs deeper.)
  const edge = g.createLinearGradient(L - tail, 0, L - tail + 0.045, 0);
  edge.addColorStop(0, 'rgba(8,34,20,0)');
  edge.addColorStop(1, 'rgb(8,34,20)');
  g.fillStyle = edge;
  g.fillRect(L - tail, -1, 1.2, W + 2);
  if (d) {
    const flat = d.createLinearGradient(L - tail, 0, L - tail + 0.045, 0);
    flat.addColorStop(0, 'rgba(60,0,110,0)');
    flat.addColorStop(1, 'rgb(60,0,110)');
    d.fillStyle = flat;
    d.fillRect(L - tail, -1, 1.2, W + 2);
  }

  // The fire: it owns the hood. It pours back off the nose and breaks over
  // the screen, running furthest down the fender tops either side.
  // (M153b: on a ride whose top-view fire stops over the snout, the scorch
  // stops with it, so the facet over the brow is dark scales, not soot.)
  const fireTo = lay.topFireTo;
  const scorch0 = Math.min(skull0 * 0.5, fireTo - 0.16);
  const scorch1 = Math.min(skull0 + 0.75, fireTo + 0.3);
  const scorch = g.createLinearGradient(scorch0, 0, scorch1, 0);
  scorch.addColorStop(0, 'rgba(5,2,1,0.97)');
  scorch.addColorStop(0.5, 'rgba(10,3,1,0.7)');
  scorch.addColorStop(1, 'rgba(10,3,1,0)');
  g.fillStyle = scorch;
  g.fillRect(-1, -1, scorch1 + 1, W + 2);
  g.fillStyle = '#070201';
  g.fillRect(-1, -1, scorch0 + 1, W + 2);
  const fire = heatLayer(g, d, SEED + 41);
  const rnd = rng(SEED + 42);
  // The body of the fire over the nose, then the licks streaming back up the
  // hood: long ones break over the screen, the fender tops carry them
  // furthest.
  // The bed only covers the FRONT of the hood: the licks must end on the
  // hood, against the scorched scales, or their points are lost under the
  // windscreen and the hood is just an orange panel (measured in the garage).
  fire.ramp(0, 0, skull0 * 0.74, 0, 0.46);
  fire.blob(0, mid, skull0 * 0.5, W * 0.8, 0.3);
  const area = (skull0 * 0.8 + 0.3) * W;
  const nBig = Math.round(10 * area) + 3;
  const nSmall = Math.round(15 * area) + 4;
  for (let i = 0; i < nBig + nSmall; i++) {
    const big = i < nBig;
    // Spread evenly across the hood (a stratified draw: no bald patches).
    const y = lerp(-0.04, W + 0.04, ((i % 9) + rnd()) / 9);
    const side = Math.abs(y - mid) / mid;
    const x = lerp(-0.3, skull0 * lerp(0.44, 0.66, side), rnd());
    const out = y < mid ? -1 : 1;
    const ang = out * side * lerp(0.02, 0.16, rnd()) + (rnd() - 0.5) * 0.14;
    fire.wisp(
      x,
      y,
      ang,
      (big ? lerp(0.5, 0.95, rnd()) : lerp(0.25, 0.5, rnd())) * lerp(0.85, 1.2, side),
      big ? lerp(0.13, 0.23, rnd()) : lerp(0.06, 0.1, rnd()),
      big ? 0.56 : 0.46,
      out * lerp(0.0, 0.3, rnd()),
    );
  }
  if (fireTo < Infinity) {
    // M153b: the heat itself fades out past the snout, so the licks end in
    // soft tips there instead of being cut.
    fire.h.save();
    fire.h.globalCompositeOperation = 'destination-in';
    const keep = fire.h.createLinearGradient(fireTo - 0.1, 0, fireTo + 0.06, 0);
    keep.addColorStop(0, 'rgba(255,255,255,1)');
    keep.addColorStop(1, 'rgba(255,255,255,0)');
    fire.h.fillStyle = keep;
    fire.h.fillRect(-2, -2, L + 4, W + 4);
    fire.h.restore();
  }
  fire.commit(0.88);
}

// ---------------------------------------------------------------------------
// Nose: the fire coming at you. Tail: the back of the neck.
// ---------------------------------------------------------------------------

function paintFront(v: LiveryViews, dv: LiveryViews | undefined, lay: Lay): void {
  const g = v.front;
  const d = dv?.front;
  const { W, H } = lay.fit;
  g.fillStyle = '#0a0301';
  g.fillRect(-1, -1, 60, 60);
  const fire = heatLayer(g, d, SEED + 61);
  const rnd = rng(SEED + 62);
  const cx = W / 2;
  const cy = H * 0.66;
  // Seen head-on: a white-hot heart low in the nose, the whole face alight
  // round it, and the flames bursting outward and climbing.
  fire.blob(cx, cy, W * 1.0, H * 1.25, 0.5);
  fire.blob(cx, cy, W * 0.36, H * 0.4, 0.5);
  const area = W * H;
  const nBig = Math.round(9 * area) + 5;
  const nSmall = Math.round(13 * area) + 6;
  for (let i = 0; i < nBig + nSmall; i++) {
    const big = i < nBig;
    const a = ((i % 12) + rnd()) * (Math.PI / 6);
    const r = lerp(0.12, 0.44, rnd());
    const x = cx + Math.cos(a) * r * W;
    const y = cy + Math.sin(a) * r * H * 0.9;
    // Outward from the heart, with a pull upward (fire climbs).
    const out = Math.atan2(y - cy - H * 0.35, (x - cx) * 0.8);
    fire.wisp(
      x,
      y,
      out + (rnd() - 0.5) * 0.4,
      big ? lerp(0.32, 0.62, rnd()) : lerp(0.16, 0.32, rnd()),
      big ? lerp(0.12, 0.2, rnd()) : lerp(0.06, 0.1, rnd()),
      big ? 0.56 : 0.46,
      (rnd() - 0.5) * 0.8,
    );
  }
  fire.commit(0.88, [3, 3]);
  ringEdge(g, d, lay);
}

/**
 * M153b — the end views' share of the beadlock rings (see the side view's
 * step 11). Where a ring's inner bevel faces fore or aft it reads an END
 * view, and which one by its POSITION: both faces of the front ring read the
 * nose painting (the fire: ragged red and orange on its bevel), the rear's
 * the tail. Under such wheels the end views' bottom corners are the same
 * old gold, so all four rings match.
 */
function ringEdge(g: G, d: G | undefined, lay: Lay): void {
  const { W, H } = lay.fit;
  if (!lay.fit.wheels.some((wh) => wh.y - wh.r * 0.5 > H)) return;
  for (const x of [-0.1, W * 0.75]) {
    g.fillStyle = '#8a7634';
    g.fillRect(x, H - 0.012, W * 0.25 + 0.1, 0.2);
    if (d) {
      d.fillStyle = 'rgb(70,0,96)';
      d.fillRect(x, H - 0.012, W * 0.25 + 0.1, 0.2);
    }
  }
}

function paintRear(v: LiveryViews, dv: LiveryViews | undefined, lay: Lay): void {
  const g = v.rear;
  const d = dv?.rear;
  const { W, H } = lay.fit;
  const mid = W / 2;
  hide(g, d);
  // Looking along the neck: scales fall away from the spine either side.
  scales(
    g,
    d,
    -0.1,
    -0.1,
    W + 0.1,
    H + 0.1,
    {
      size: (x) => lerp(0.17, 0.12, clamp01(Math.abs(x - mid) / mid)),
      dir: (x) => ((x - mid) / mid) * -0.5,
      tone: (x, y) => 0.55 - Math.abs((x - mid) / mid) * 0.6 - (y / H) * 0.75,
      keel: true,
      down: true,
    },
    SEED + 71,
  );
  for (let y = H + 0.05; y > -0.1; y -= 0.17) {
    spike(g, [mid, y - 0.13], [mid, y + 0.11], 0.11, 0, HORN, d, { shadow: 0.008, data: 'rgb(244,0,80)', lit: 1 });
  }
  const fade = g.createLinearGradient(0, H * 0.55, 0, H);
  fade.addColorStop(0, 'rgba(0,0,0,0)');
  fade.addColorStop(1, 'rgba(0,0,0,0.5)');
  g.fillStyle = fade;
  g.fillRect(-1, H * 0.55, W + 2, H);
  // M153: the top edge is one flat tone, like the top view's tail edge: the
  // back face of a bolt-on above the body (the Formula's rear wing) reads the
  // livery off this edge and wore a row of chopped scales and a bone bar.
  const edge = g.createLinearGradient(0, 0.03, 0, 0.008);
  edge.addColorStop(0, 'rgba(8,34,20,0)');
  edge.addColorStop(1, 'rgb(8,34,20)');
  g.fillStyle = edge;
  g.fillRect(-1, -1, W + 2, 1.03);
  if (d) {
    const flat = d.createLinearGradient(0, 0.03, 0, 0.008);
    flat.addColorStop(0, 'rgba(60,0,110,0)');
    flat.addColorStop(1, 'rgb(60,0,110)');
    d.fillStyle = flat;
    d.fillRect(-1, -1, W + 2, 1.03);
  }
  ringEdge(g, d, lay);
}

/**
 * The shop card: the dragon's own face — jaws, eye, brow — with the fire at
 * its lips. Painted with the wrap's own painter on a tall flank, then cropped
 * to the head, so the card is literally a piece of the wrap.
 */
export function dragonCardCanvas(w = 360, hgt = 240): HTMLCanvasElement {
  const fit: BodyFit = { L: 4.2, H: 1, W: 1.7, belt: 0.93, wheels: [], vehicle: 'card' };
  const lay = layout(fit);
  const k = 2; // paint at twice the card, then resolve down
  const px = Math.round((hgt * k) / fit.H); // pixels per metre, square
  const side = document.createElement('canvas');
  side.width = Math.round(fit.L * px);
  side.height = Math.round(fit.H * px);
  const g = side.getContext('2d')!;
  g.scale(px, px);
  const views = { side: g, top: g, front: g, rear: g, fit } as LiveryViews;
  paintSide(views, undefined, lay);
  const c = document.createElement('canvas');
  c.width = w;
  c.height = hgt;
  const cg = c.getContext('2d')!;
  // A 3:2 window on the head: a lick of fire at the left, the eye inside.
  const wide = (w / hgt) * fit.H;
  const x0 = lay.mouthX + lay.headLen * 0.34 - wide / 2;
  cg.imageSmoothingEnabled = true;
  cg.imageSmoothingQuality = 'high';
  // Rounded so it sits in the card like the jerseys do, not as a hard tile.
  const r = hgt * 0.1;
  cg.beginPath();
  cg.moveTo(r, 0);
  cg.arcTo(w, 0, w, hgt, r);
  cg.arcTo(w, hgt, 0, hgt, r);
  cg.arcTo(0, hgt, 0, 0, r);
  cg.arcTo(0, 0, w, 0, r);
  cg.closePath();
  cg.clip();
  cg.drawImage(side, x0 * px, 0, wide * px, fit.H * px, 0, 0, w, hgt);
  return c;
}

export const dragonPainter: LiveryPainter = (v, dv) => {
  const lay = layout(v.fit);
  paintSide(v, dv, lay);
  paintTop(v, dv, lay);
  paintFront(v, dv, lay);
  paintRear(v, dv, lay);
};
